// PARTNER-PORTÁL · jegy-részlet — a terv szerint
// (a partner-terv szerint — a MEGÉPÜLT állapot: `docs/foundations/design.md` §9).
//
// SZERKEZET: fejléc (cím + KÉT META-SOR: azonosító adat, majd állapot) → KÉT HASÁB:
//   • bal: Kérés (leírás + mellékletek + fájlmegosztó linkek) · Kommentek (szál + ragadós komment-író)
//   • jobb: Előzmény · „Ki látja ezt a jegyet" (a hozzáférés-osztó gombbal a fejlécében)
//
// AMI SZÁNDÉKOSAN NINCS a tervhez képest: „Újranyitás" gomb. A terv mockupja mutatja, de a user-döntés
// szerint a partner NEM állít státuszt — ezért végpont sincs rá, és az Odin RLS-e is tiltja
// (partner-fal a `support.tickets` UPDATE-en). Egy gomb, ami 403-at kap, rosszabb, mint a hiánya.
import type { PortalTicketDetail } from '@atlas/contracts';
import { PORTAL_PERMISSIONS } from '@atlas/shared';
import {
  ArrowDownTrayIcon,
  BuildingOffice2Icon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  CloudIcon,
  DocumentIcon,
  EnvelopeOpenIcon,
  LockClosedIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { PortalErrorState } from '@/components/portal/portal-error-state';
import { PortalPermissionGate } from '@/components/portal/portal-permission-gate';
import { PortalTimeline, type TimelineItem } from '@/components/portal/portal-timeline';
import { CardRow, CardRows } from '@/components/shared/card-rows';
import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { FilePickButton } from '@/components/shared/file-pick-button';
import { GatedAction } from '@/components/shared/gated-action';
import { MetaDot } from '@/components/shared/meta-dot';
import { PanelCard } from '@/components/shared/panel-card';
import { PersonChip } from '@/components/shared/person-chip';
import { TicketStatusBadge } from '@/components/tickets/ticket-status-badge';
import { TicketTypeBadge } from '@/components/tickets/ticket-type-badge';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Divider } from '@/components/ui/divider';
import { RichTextContent } from '@/components/ui/rich-text-content';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { portalKeys } from '@/features/portal/api';
import { uploadPortalTicketAttachment } from '@/features/portal/attachment-upload';
import { PortalTicketAccessSheet } from '@/features/portal/components/portal-ticket-access-sheet';
import { PortalTicketComment } from '@/features/portal/components/portal-ticket-comment';
import { PortalTicketComposer } from '@/features/portal/components/portal-ticket-composer';
import { portalTicketImageResolver } from '@/features/portal/editor-images';
import { usePortalHead } from '@/features/portal/portal-context';
import { useAttachmentUploader } from '@/hooks/use-attachment-uploader';
import { useDraft } from '@/hooks/use-draft';
import { api } from '@/lib/api';
import { ATTACHMENT_ACCEPT, MAX_ATTACHMENTS } from '@/lib/attachment-rules';
import type { EditorImageResolverConfig } from '@/lib/editor-image-src';
import { toHref } from '@/lib/external-url';
import { formatDateTime } from '@/lib/format-datetime';
import { downloadFresh } from '@/lib/fresh-download';
import { isRichTextBlank, looksLikeStoredHtml } from '@/lib/sanitize-html';
import { useEntityMutation } from '@/lib/use-entity-mutation';

export const Route = createFileRoute('/portal/tickets/$id')({
  component: PortalTicketDetailPage,
});

/** A tárolt törzs: HTML → sanitizált rich-text, migrált sima szöveg → sortörés-megőrzéssel. */
function StoredBody({
  body,
  imageResolver,
}: {
  body: string;
  imageResolver?: EditorImageResolverConfig | undefined;
}): JSX.Element {
  return looksLikeStoredHtml(body) ? (
    // A BEÁGYAZOTT KÉPEK a jegyhez KÖTÖTT feloldón jelennek meg: a tárolt HTML a stabil utat viseli, amit a
    // böngésző nem tud betölteni (a BFF Bearer-tokent kér). A belső feloldó a partnernek nincs nyitva — az
    // bármely fájl-id-t feloldaná; a jegyhez kötött viszont csak akkor ad URL-t, ha a fájl tényleg ennek a
    // látható jegynek a szövegében szerepel.
    <RichTextContent html={body} {...(imageResolver ? { imageResolver } : {})} />
  ) : (
    <p className="whitespace-pre-line text-sm leading-[1.65]">{body}</p>
  );
}

/** A lap a jog-kapun át: jog nélkül magyarázat áll a lap helyett (a fal a BFF-ben van). */
function PortalTicketDetailPage(): JSX.Element {
  return (
    <PortalPermissionGate permission={PORTAL_PERMISSIONS.ticketsView}>
      <PortalTicketDetailPageBody />
    </PortalPermissionGate>
  );
}

function PortalTicketDetailPageBody(): JSX.Element {
  const { id } = Route.useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  // AUTOMATIKUS VÁZLAT: a félbehagyott válasz egy frissítést / navigálást túlél; küldés vagy „Mégse" után törlődik
  const draft = useDraft<string>(`portal-ticket:${id}`, {
    onRestore: (value) => setReply(value ?? ''),
  });
  const updateReply = (next: string): void => {
    setReply(next);
    draft.save(next, isRichTextBlank(next));
  };
  const [accessOpen, setAccessOpen] = useState(false);

  // A HÍVÓ identity-je (a `/session` `sub`-ja, ami a portálon is elérhető — `@AllowPartner()`): az optimista
  // reakció-frissítés ebből tudja, KINEK a reakcióját teszi be/ki. A szerkesztés jogát NEM ez dönti: azt a
  // szerver adja meg soronként (`canEdit`).
  // A jegy-tartalom beágyazott képeinek feloldója (a leírás és a válaszok HTML-jében). `useMemo`, mert a
  // `scope` a cache-kulcs része — új objektum minden renderben új feloldást indítana.
  const imageResolver = useMemo(() => portalTicketImageResolver(id), [id]);

  const detailKey = portalKeys.ticketDetail(id);
  const q = useQuery({
    queryKey: detailKey,
    queryFn: () =>
      api.portal.ticketDetail.query({
        params: { id },
        query: { lang: i18n.language === 'en' ? 'en' : 'hu' },
      }),
  });
  const ticket: PortalTicketDetail | undefined = q.data?.status === 200 ? q.data.body : undefined;
  const isForbidden = q.data?.status === 403;
  const isNotFound = q.data?.status === 404;
  const isError = q.data !== undefined && q.data.status !== 200 && !isForbidden && !isNotFound;

  // UTÓLAGOS FÁJL-CSATOLÁS a megnyitott jegyhez (Évi #82): a válasz-joggal bíró partner a jegy-lapon is csatolhat
  // (a BFF-végpont a nyitó űrlap óta megvan). A kliens-validáció + sorban feltöltés + toastok a KÖZÖS hookban.
  const uploader = useAttachmentUploader({
    existingCount: ticket?.attachments.length ?? 0,
    onUpload: (file) => uploadPortalTicketAttachment(id, file),
    onChanged: () => void queryClient.invalidateQueries({ queryKey: detailKey }),
    messages: {
      limit: t('toast:portal.attachmentLimit', { count: MAX_ATTACHMENTS }),
      invalidType: t('toast:portal.attachmentAddInvalidType'),
      tooLarge: t('toast:portal.attachmentAddTooLarge'),
      uploaded: t('toast:portal.attachmentUploaded'),
      uploadFailed: t('toast:portal.attachmentAddFailed'),
      forbidden: t('toast:portal.forbidden'),
      validation: t('toast:portal.validation'),
      unavailable: t('toast:portal.attachmentAddFailed'),
    },
  });

  // MORZSA a topbarban: „Jegyeim / #TKT-…" + a fiók az alcímben (a terv `ticketHead`-je).
  usePortalHead(
    {
      crumbs: [
        { label: t('portal.tickets.title'), to: '/portal/tickets' },
        { label: ticket?.ticketNumber ?? t('portal.tickets.detailFallback') },
      ],
      subtitle: ticket?.accountName,
    },
    [ticket?.ticketNumber, ticket?.accountName, t('portal.tickets.title')],
  );

  // A SZÁL MEGNYITÁSA olvasottra állít — egyszer jegyenként (a `useRef` a duplikált POST-ot zárja ki). A
  // hibája SZÁNDÉKOSAN néma: a partner már látja a tartalmat, a következő megnyitás újra próbálja.
  const markedRef = useRef<string | null>(null);
  const unreadCount = ticket?.comments.filter((c) => c.unread).length ?? 0;
  useEffect(() => {
    if (ticket === undefined || unreadCount === 0 || markedRef.current === id) return;
    markedRef.current = id;
    void api.portal.ticketMarkRead
      .mutation({ params: { id } })
      .then((res) => {
        if (res.status !== 200) return;
        void queryClient.invalidateQueries({ queryKey: portalKeys.tickets() });
      })
      .catch(() => undefined);
  }, [ticket, unreadCount, id, queryClient]);

  const replyMutation = useEntityMutation<{ body: string }, typeof q.data>({
    mutationFn: (v) => api.portal.ticketReply.mutation({ params: { id }, body: v }),
    invalidateKeys: [detailKey, portalKeys.tickets()],
    // OPTIMISTA: a válasz AZONNAL megjelenik a szálon; hibára a hook visszaállítja a snapshotot.
    optimistic: {
      queryKey: detailKey,
      update: (previous) => {
        if (previous?.status !== 200) return previous;
        return {
          ...previous,
          body: {
            ...previous.body,
            comments: [
              ...previous.body.comments,
              {
                id: `optimistic-${String(previous.body.comments.length)}`,
                // A szerkesztő HTML-t ad — a `reply` már a tárolt alak (nem trimmelt sima szöveg).
                body: reply,
                authorName: null,
                // Az OPTIMISTA soron nincs kép (a szerver válasza hozza a feloldott avatart) → monogram.
                authorAvatarUrl: null,
                authorTeamColor: null,
                authorIsPartner: true,
                createdAt: new Date().toISOString(),
                editedAt: null,
                unread: false,
                // Az OPTIMISTA sor még nem létezik a szerveren: nincs mire reagálni, és nincs OCC-verzió,
                // amivel szerkeszteni lehetne. A `PortalTicketComment` ezért az `optimistic-` id-re
                // egyáltalán nem rajzol akció-sort — a szerver válasza után jön az igazi sor.
                reactions: [],
                canEdit: false,
                version: 0,
              },
            ],
          },
        };
      },
    },
    messages: {
      success: t('toast:portal.replySent'),
      conflict: t('toast:portal.error'),
      forbidden: t('toast:portal.forbidden'),
      validation: t('toast:portal.validation'),
      error: t('toast:portal.error'),
    },
  });

  const timelineItems: TimelineItem[] = useMemo(() => {
    const ICONS = {
      opened: EnvelopeOpenIcon,
      reply: ChatBubbleLeftRightIcon,
      status: ArrowPathIcon,
      closed: CheckCircleIcon,
    } as const;
    return (ticket?.events ?? []).map((e, i) => ({
      id: `${e.kind}-${String(i)}`,
      label: t(`portal.tickets.event.${e.kind}`),
      meta: [formatDateTime(e.at, i18n.language), e.by].filter(Boolean).join(' · '),
      Icon: ICONS[e.kind],
      tone:
        e.kind === 'closed'
          ? ('success' as const)
          : e.kind === 'opened'
            ? ('brand' as const)
            : undefined,
    }));
  }, [ticket?.events, t, i18n.language]);

  if (q.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-2/3" />
        <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
          <Skeleton className="h-64 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      </div>
    );
  }
  if (isForbidden) {
    return (
      <EmptyState title={t('portal.noAccess.title')} description={t('portal.noAccess.desc')} />
    );
  }
  // NEM LÁTHATÓ vagy nem létező jegy → UGYANAZ az állapot (a szerver sem különbözteti meg: 404 mindkettőre).
  if (isNotFound) {
    return (
      <EmptyState
        title={t('portal.tickets.notFound.title')}
        description={t('portal.tickets.notFound.desc')}
        action={
          <Button variant="outline" onClick={() => void navigate({ to: '/portal/tickets' })}>
            {t('portal.tickets.backToList')}
          </Button>
        }
      />
    );
  }
  if (isError || ticket === undefined) {
    return <PortalErrorState onRetry={() => void q.refetch()} />;
  }

  const links = [
    { key: 'googleDrive', url: ticket.googleDriveLink },
    { key: 'onedrive', url: ticket.microsoftOnedriveLink },
    { key: 'dropbox', url: ticket.dropboxLink },
  ].filter((l): l is { key: string; url: string } => typeof l.url === 'string' && l.url !== '');
  const waitingOnUs = ticket.status === 'in_progress';
  // AKI LÁTJA A JEGYET: a hozzáférést kapott kapcsolattartók + a fiók elsődlegese (ő a láthatósági mag 1.
  // ága szerint mindent lát). A szerver adat-minimalizálásból CSAK annak adja meg a névsort, aki oszthat —
  // üres lista tehát nem azt jelenti, hogy senki nem látja, hanem hogy nem a mi dolgunk tudni.
  const visibleContacts = ticket.contacts.filter((c) => c.hasAccess || c.isPrimary);

  return (
    <div className="w-full max-w-[1180px] space-y-4">
      {/* VISSZA-GOMB ITT SZÁNDÉKOSAN NINCS: a lap MORZSÁT kap a topbarban („Jegyeim / #TKT-…"), és az a
          visszaút — a terv is így oldja meg (`design/partner/app.jsx` `ticketHead.crumbs`). A lap-testbe tett
          extra „Vissza a listához" gomb a régi (Laravel) felület mintája volt: két visszaút ugyanoda, az
          egyik a tartalom elé tolva. Mérve 2026-09-02: a user épp ezt jelezte, hogy „régi design-ra hajaz".

          FEJLÉC: a jelzések a cím ELŐTT állnak (a terv sorrendje) — a partner először azt látja, hol tart a
          jegy, és csak utána a tárgyat. */}
      {/* FEJLÉC — a CÍM alatt KÉT META-SOR, a belső jegy-részlet mintájára (user-döntés): az „Adatok"
          kártya kivezetve, mert ugyanazt a hat adatot egy jobb-hasábos panelben KÉTSZER olvastuk (a
          jelvények már a fejlécben álltak), és a panel a lap legértékesebb helyét foglalta. A KÉT SOR
          szétválasztása szándékos: az 1. sor az AZONOSÍTÓ adat („mi ez, ki nyitotta, melyik fiókra"), a 2.
          az ÁLLAPOT („hol tart, kinél van"). Egy sorban a hét elem tördelt, és a két olvasat összefolyt. */}
      <div className="min-w-0 space-y-2.5">
        <h1 className="text-[23px] font-semibold leading-tight tracking-[-0.02em]">
          {ticket.subject}
        </h1>

        {/* 1. SOR — típus · „ki nyitotta és mikor" · a fiók. A csoportok között TÉRKÖZ tagol; pont CSAK az
            igénylő és a nyitás ideje között áll, mert azok egy gondolatot mondanak. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
          <TicketTypeBadge
            type={ticket.type}
            label={t(`portal.type.${ticket.type}`, { defaultValue: ticket.type })}
          />
          {(ticket.requesterName !== null || ticket.createdAt !== '') && (
            <span className="text-muted-foreground inline-flex min-w-0 items-center gap-x-2">
              {ticket.requesterName !== null && (
                <PersonChip
                  name={ticket.requesterName}
                  givenName={ticket.requesterGivenName}
                  familyName={ticket.requesterFamilyName}
                  avatarUrl={ticket.requesterAvatarUrl}
                  size="sm"
                  className="min-w-0 gap-1.5 text-sm"
                />
              )}
              {ticket.requesterName !== null && ticket.createdAt !== '' && <MetaDot />}
              {ticket.createdAt !== '' && (
                <span>{formatDateTime(ticket.createdAt, i18n.language)}</span>
              )}
            </span>
          )}
          <span className="text-muted-foreground inline-flex min-w-0 items-center gap-1.5">
            <BuildingOffice2Icon className="size-3.5 shrink-0" aria-hidden="true" />
            <span className="min-w-0 truncate">{ticket.accountName}</span>
          </span>
        </div>

        {/* 2. SOR — az ÁLLAPOT: státusz · „kinél van a sor" jelzés · felelős. A státusz itt NEM kattintható
            (a partner nem állít státuszt — nincs is rá végpont), ezért jelvény, nem vezérlő. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
          <span className="inline-flex items-center gap-2">
            <TicketStatusBadge
              status={ticket.status}
              label={t(`portal.status.${ticket.status}`)}
              help={t(`portal.statusHelp.${ticket.status}`)}
            />
            {waitingOnUs ? (
              <DomainBadge kind="ticketTurn" value="us" label={t('portal.tickets.onUs')} />
            ) : ticket.status === 'open' ? (
              <DomainBadge
                kind="ticketTurn"
                value="partner"
                label={t('portal.tickets.awaitingReply')}
              />
            ) : null}
          </span>
          <span className="text-muted-foreground inline-flex min-w-0 items-center gap-2">
            <span>{t('portal.tickets.field.assignee')}:</span>
            {ticket.assigneeName === null ? (
              <span>{t('portal.tickets.noAssignee')}</span>
            ) : (
              <PersonChip
                name={ticket.assigneeName}
                avatarUrl={ticket.assigneeAvatarUrl}
                accentColor={ticket.assigneeTeamColor}
                size="sm"
                className="min-w-0 gap-1.5 text-sm"
              />
            )}
          </span>
        </div>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-[2fr_1fr]">
        <div className="flex flex-col gap-4">
          {/* KÉRÉS — a nyitáskor rögzített leírás, ÉS ALATTA a hozzá tartozó FÁJLOK: a mellékletek és a
              fájl-megosztási linkek (user-döntés): mindhárom UGYANARRÓL szól — „mit adott le a partner" —,
              és három külön kártyában a jobb hasáb mellett a lap fölöslegesen hosszúra nyúlt, két
              majdnem-üres panellel. A belső jegy-részlet is így sorolja: leírás → mellékletek → linkek. A
              blokkokat BEHÚZOTT hajszálvonal tagolja, és üresen MEG SEM JELENNEK. */}
          <PanelCard
            title={t('portal.tickets.request')}
            action={
              <Text variant="secondary" as="span">
                {formatDateTime(ticket.createdAt, i18n.language)}
              </Text>
            }
          >
            <div className="p-4">
              <StoredBody body={ticket.description} imageResolver={imageResolver} />
            </div>

            {/* A blokk válasz-joggal ÜRESEN is látszik — a csatolás gombja itt van (Évi #82). */}
            {(ticket.attachments.length > 0 || ticket.canReply) && (
              <>
                <Divider spacing="none" />
                <div className="px-4 pb-1 pt-3.5">
                  <div className="mb-2 flex items-center gap-2">
                    <Text variant="overline" as="span">
                      {t('portal.tickets.attachments')}
                    </Text>
                    <Badge variant="outline">{ticket.attachments.length}</Badge>
                    {ticket.canReply && ticket.attachments.length < MAX_ATTACHMENTS && (
                      <FilePickButton
                        inputRef={uploader.inputRef}
                        accept={ATTACHMENT_ACCEPT}
                        busy={uploader.busy}
                        label={t('portal.tickets.attachFile')}
                        busyLabel={t('portal.tickets.attachUploading')}
                        onFiles={(files) => void uploader.onFiles(files)}
                        className="ml-auto"
                      />
                    )}
                  </div>
                  {ticket.attachments.length === 0 && (
                    <Text variant="secondary" as="p" className="pb-2">
                      {t('portal.tickets.attachmentsEmpty')}
                    </Text>
                  )}
                  <CardRows>
                    {ticket.attachments.map((a) => (
                      <CardRow key={a.id}>
                        {/* A LETÖLTÉS friss presigned URL-lel, KÖZVETLENÜL a tárból — KATTINTÁSKOR kérve, mert a
                        részletben kapott URL percek alatt lejár (Évi #79). `downloadUrl` nélkül (ETL-örökölt
                        sor) nincs link — halott linket nem kínálunk. */}
                        {a.downloadUrl !== null ? (
                          <a
                            href={a.downloadUrl}
                            target="_blank"
                            rel="noreferrer"
                            download={a.filename}
                            onClick={(e) => {
                              e.preventDefault();
                              void downloadFresh(async () => {
                                const res = await api.portal.ticketAttachmentDownloadUrl.query({
                                  params: { id: ticket.id, attachmentId: a.id },
                                });
                                return res.status === 200 ? res.body.url : null;
                              }).then((ok) => {
                                if (!ok) toast.error(t('toast:portal.attachmentDownloadFailed'));
                              });
                            }}
                            className="hover:bg-accent flex items-center gap-3 px-4 py-2.5 transition-colors"
                          >
                            <span className="bg-muted text-muted-foreground flex size-[30px] shrink-0 items-center justify-center rounded-[9px]">
                              <DocumentIcon className="size-4" />
                            </span>
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <Text variant="itemTitle" as="span" className="truncate">
                                {a.filename}
                              </Text>
                              <Text variant="secondary" as="span">
                                {formatDateTime(a.createdAt, i18n.language)}
                              </Text>
                            </span>
                            <ArrowDownTrayIcon className="text-muted-foreground size-4 shrink-0" />
                          </a>
                        ) : (
                          <div className="flex items-center gap-3 px-4 py-2.5">
                            <span className="bg-muted text-muted-foreground flex size-[30px] shrink-0 items-center justify-center rounded-[9px]">
                              <DocumentIcon className="size-4" />
                            </span>
                            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                              <Text variant="itemTitle" as="span" className="truncate">
                                {a.filename}
                              </Text>
                              <Text variant="secondary" as="span">
                                {t('portal.tickets.downloadUnavailable')}
                              </Text>
                            </span>
                          </div>
                        )}
                      </CardRow>
                    ))}
                  </CardRows>
                </div>
              </>
            )}

            {links.length > 0 && (
              <>
                <Divider spacing="none" />
                <div className="px-4 pb-1 pt-3.5">
                  <Text variant="overline" as="div" className="mb-2">
                    {t('portal.tickets.links')}
                  </Text>
                  <CardRows>
                    {links.map((l) => (
                      <CardRow key={l.key}>
                        <a
                          href={toHref(l.url)}
                          target="_blank"
                          rel="noreferrer"
                          className="hover:bg-accent flex items-center gap-3 px-4 py-2.5 transition-colors"
                        >
                          <span className="bg-sky-soft text-sky flex size-[30px] shrink-0 items-center justify-center rounded-[9px]">
                            <CloudIcon className="size-4" />
                          </span>
                          <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                            <Text variant="itemTitle" as="span">
                              {t(`portal.tickets.linkLabel.${l.key}`)}
                            </Text>
                            <Text variant="secondary" as="span" className="truncate">
                              {l.url}
                            </Text>
                          </span>
                          <ArrowTopRightOnSquareIcon className="text-muted-foreground size-4 shrink-0" />
                        </a>
                      </CardRow>
                    ))}
                  </CardRows>
                </div>
              </>
            )}
          </PanelCard>

          {/* KOMMENTEK — a szál. A fejléc-cím „Kommentek" (user-döntés): a „Válaszok" a legacy szóhasználata
              volt, és a belső felület is kommentet mond — egy fogalom, egy szó a két felületen. */}
          <PanelCard
            title={t('portal.tickets.comments')}
            badge={<Badge variant="outline">{ticket.comments.length}</Badge>}
            // A RAGADÓS komment-sáv miatt: a `Card` `overflow-hidden`-je különben a sticky gyerek
            // scroll-konténere lenne, és a sáv a kártya aljához tapadna — vagyis oda, ahol amúgy is áll.
            stickyContent
          >
            {/* `pt-3.5`: a fejléc alatti hajszálvonal és az ELSŐ komment-kártya közé levegő kell — korábban
                az első bejegyzés a vonalhoz tapadt (user-lelet).

                `pb-6`: az UTOLSÓ kártya AKCIÓ-SORA a szegélyen ÜL, és `-bottom-3`-mal LEFELÉ lóg — a ragadós
                komment-sáv (átlátszatlan `bg-card`) különben pont a chipek alsó felét vágta le
                (user-lelet). MÉRVE: 16px-nél a hézag már pozitív (4px), de szoros — 24px-nél 12px levegő
                marad a sor alja és a sáv teteje között, ami a szál végén amúgy is jólesik. */}
            <div className="px-4 pb-6 pt-3.5">
              {ticket.comments.length === 0 ? (
                <Text variant="secondary" as="p" className="py-4">
                  {t('portal.tickets.noComments')}
                </Text>
              ) : (
                // KÁRTYÁS bejegyzések → a tagolást a SZEGÉLY adja, nem hajszálvonal, tehát a sorok közé
                // TÉRKÖZ kell (`space-y-4`, nem `divide-*`). A 4-es köz azért nem szűkebb, mert az akció-sor
                // a kártya ALSÓ szegélyére lóg (`-bottom-3`) — szűkebb közben a következő kártyára feküdt volna.
                <div
                  aria-label={t('portal.tickets.conversationAria')}
                  role="list"
                  className="space-y-4"
                >
                  {ticket.comments.map((c) => (
                    <div role="listitem" key={c.id}>
                      <PortalTicketComment
                        comment={c}
                        ticketId={id}
                        accountId={ticket.accountId}
                        // A REAGÁLÁS ÍRÁS: ugyanaz a jog dönt, mint a kommentelésen (a `portal.tickets.write` nélküli szerep nem ír,
                        // és a szerver is 403-at ad).
                        canReact={ticket.canReply}
                        imageResolver={imageResolver}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* KOMMENT-ÍRÁS — RAGADÓS sávban, hogy hosszú szálon se kelljen az aljára görgetni. Csak
                `canReply` esetén (a szerver ugyanezt érvényesíti: a `viewer` 403-at kap). */}
            {ticket.canReply ? (
              <PortalTicketComposer
                accountId={ticket.accountId}
                value={reply}
                onChange={updateReply}
                draftSavedAt={draft.savedAt}
                pending={replyMutation.isPending}
                onSubmit={() =>
                  replyMutation.mutate(
                    { body: reply },
                    {
                      onSuccess: () => {
                        draft.discard();
                        setReply('');
                      },
                    },
                  )
                }
              />
            ) : (
              <div className="p-4 pt-2">
                <div className="bg-muted/40 text-muted-foreground flex items-start gap-2.5 rounded-xl p-3 text-[12.5px] leading-relaxed">
                  <LockClosedIcon className="mt-0.5 size-4 shrink-0" />
                  <span>{t('portal.tickets.readOnly')}</span>
                </div>
              </div>
            )}
          </PanelCard>
        </div>

        <div className="flex flex-col gap-4">
          <PanelCard title={t('portal.tickets.history')}>
            <div className="p-4">
              <PortalTimeline items={timelineItems} />
            </div>
          </PanelCard>

          {/* KI LÁTJA EZT A JEGYET — a lap egyetlen hozzáférés-felülete (user-döntés): a fejlécből kivett
              „Jegy-hozzáférés" gomb IDE került, a hozzá tartozó ADAT mellé. MIÉRT jobb így: a fejléc-gomb
              önmagában nem mondta meg, mi a jelenlegi állapot — a partner rákattintott, hogy megtudja, ki
              látja a jegyet. A kártya kimondja (névsor + „Ön" / elsődleges jelöléssel), és a MÓDOSÍTÁS ott
              van, ahol az állapot: ez az iparági minta (GitHub „Assignees", Linear „Subscribers", Notion
              „Share").

              A KÁRTYA MINDIG LÁTSZIK, akkor is, ha a hívó nem oszthat: a „kik látják" KÉRDÉS mindenkinek
              legitim. Amit a szerver adat-minimalizálásból nem ad meg (a többi kapcsolattartó névsorát csak
              a nyitó / elsődleges kapja), azt egy magyarázó sor helyettesíti — üres panel helyett.
              A MŰVELET a repo konvenciója szerint nincs elrejtve, hanem TILTVA, magyarázó tooltippel
              (`design.md` → „Jog-kapuzás"): a jog TULAJDONLÁS-alapú, és a szerver ugyanezt érvényesíti. */}
          <PanelCard
            title={t('portal.tickets.visibilityTitle')}
            action={
              <GatedAction
                allowed={ticket.canManageAccess}
                reason={t('portal.tickets.accessDisabled')}
              >
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!ticket.canManageAccess}
                  onClick={() => setAccessOpen(true)}
                >
                  <UsersIcon />
                  {t('portal.tickets.accessManage')}
                </Button>
              </GatedAction>
            }
          >
            {/* `text-sm`: a `PersonChip` a KÖRNYEZŐ tipográfiát örökli — a kártya-törzs alap 15px-én a nevek
                túl nagyok voltak (user-mérés). */}
            <div className="flex flex-col gap-2.5 p-4 text-sm">
              {/* A LÁTHATÓSÁG SZABÁLYA egy sorban, a névsor ELŐTT: a partner ebből tudja, MIÉRT az, aki
                  ott van (a négy ág közül melyik érvényes rá). Enélkül a névsor önmagában nem magyaráz. */}
              <Text variant="secondary" as="p" className="text-[12.5px] leading-relaxed">
                {t('portal.tickets.visibilityHint')}
              </Text>
              <Divider spacing="none" inset={false} className="opacity-60" />
              {visibleContacts.length === 0 ? (
                <Text variant="secondary" as="span">
                  {ticket.canManageAccess
                    ? t('portal.tickets.accessOnlyYou')
                    : t('portal.tickets.visibilityUnknown')}
                </Text>
              ) : (
                visibleContacts.map((c) => (
                  <div key={c.identityId} className="flex items-center gap-2">
                    <PersonChip
                      name={c.name}
                      givenName={c.givenName}
                      familyName={c.familyName}
                      avatarUrl={c.avatarUrl}
                    />
                    <span className="ml-auto flex items-center gap-1.5">
                      {/* „Ön" a hívó saját során (a terv jelölése) — a saját hozzáférés amúgy sem vonható meg. */}
                      {c.isSelf && (
                        <DomainBadge kind="marker" value="you" label={t('portal.tickets.you')} />
                      )}
                      {c.isPrimary && (
                        <DomainBadge
                          kind="marker"
                          value="primary"
                          label={t('portal.profile.primaryShort')}
                        />
                      )}
                    </span>
                  </div>
                ))
              )}
            </div>
          </PanelCard>
        </div>
      </div>

      <PortalTicketAccessSheet
        open={accessOpen}
        onOpenChange={setAccessOpen}
        ticketId={id}
        contacts={ticket.contacts}
      />
    </div>
  );
}
